import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useEffect } from "react";
import { useAnalyticsIdentity } from "@/hooks/useAnalytics";
import analytics from "@/lib/segment";
import Landing from "./pages/Landing";
import TuroHostTools from "./pages/TuroHostTools";
import Pricing from "./pages/Pricing";
import CarRentalCoach from "./pages/CarRentalCoach";
import Auth from "./pages/Auth";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import CaptainAI from "./pages/CaptainAI";
import Vehicles from "./pages/Vehicles";
import Maintenance from "./pages/Maintenance";
import Expenses from "./pages/Expenses";
import Playbooks from "./pages/Playbooks";
import PlaybookDetail from "./pages/PlaybookDetail";
import PublicPlaybook from "./pages/PublicPlaybook";
import Team from "./pages/Team";
import ThankYou from "./pages/ThankYou";
import OnboardingSurvey from "./pages/OnboardingSurvey";
import AdminPlaybooks from "./pages/admin/AdminPlaybooks";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminPlaybookEdit from "./pages/admin/AdminPlaybookEdit";
import AdminPlaybookNodes from "./pages/admin/AdminPlaybookNodes";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import Settings from "./pages/Settings";
import Earnings from "./pages/Earnings";
import Upgrade from "./pages/Upgrade";
import LaunchKit from "./pages/LaunchKit";
import LaunchKitArticle from "./pages/LaunchKitArticle";
import Support from "./pages/Support";
import DecisionPlaceholder from "./pages/decisions/DecisionPlaceholder";
import UnderperformingVehicles from "./pages/decisions/UnderperformingVehicles";
import VehicleComparisons from "./pages/decisions/VehicleComparisons";
import ProfitOpportunities from "./pages/decisions/ProfitOpportunities";
import AttentionRequired from "./pages/decisions/AttentionRequired";
import WhatCouldGoWrong from "./pages/decisions/WhatCouldGoWrong";
import AlertsExceptions from "./pages/decisions/AlertsExceptions";

import FleetOverview from "./pages/operations/FleetOverview";
import SetupProgress from "./pages/orientation/SetupProgress";
import BasicSignals from "./pages/orientation/BasicSignals";
import GuidanceNextSteps from "./pages/orientation/GuidanceNextSteps";
import Documentation from "./pages/Documentation";
import TuroNews from "./pages/TuroNews";
import CheckoutRedirect from "./pages/CheckoutRedirect";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";

const queryClient = new QueryClient();

// Component that tracks page views and scrolls to top on route changes
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    analytics.page(undefined, undefined, {
      path: location.pathname,
      search: location.search,
    });
  }, [location.pathname, location.search]);

  return null;
};

// Inner component that uses hooks
const AppContent = () => {
  // Initialize Segment analytics identity tracking
  useAnalyticsIdentity();

  return (
    <>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <PageViewTracker />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/turo-host-tools" element={<TuroHostTools />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/car_rental_coach" element={<CarRentalCoach />} />
          <Route path="/docs" element={<Documentation />} />
          <Route path="/docs/:articleId" element={<Documentation />} />
          <Route path="/news" element={<TuroNews />} />
          <Route path="/news/:articleId" element={<TuroNews />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/share/:slug" element={<PublicPlaybook />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/checkout-redirect" element={<CheckoutRedirect />} />
          <Route path="/onboarding-survey" element={<OnboardingSurvey />} />
          
          {/* Authenticated app routes */}
          <Route path="/app/launch-kit" element={<LaunchKit />} />
          <Route path="/app/launch-kit/:articleId" element={<LaunchKitArticle />} />
          <Route path="/app/dashboard" element={<Dashboard />} />
          <Route path="/app/captain" element={<CaptainAI />} />
          <Route path="/app/vehicles" element={<Vehicles />} />
          <Route path="/app/maintenance" element={<Maintenance />} />
          <Route path="/app/expenses" element={<Expenses />} />
          <Route path="/app/playbooks" element={<Playbooks />} />
          <Route path="/app/playbooks/:playbookId" element={<PlaybookDetail />} />
          <Route path="/app/team" element={<Team />} />
          <Route path="/app/settings" element={<Settings />} />
          <Route path="/app/support" element={<Support />} />
          <Route path="/app/earnings" element={<Earnings />} />
          <Route path="/app/upgrade" element={<Upgrade />} />
          <Route path="/app/orientation/setup" element={<SetupProgress />} />
          <Route path="/app/orientation/signals" element={<BasicSignals />} />
          <Route path="/app/orientation/next" element={<GuidanceNextSteps />} />
          <Route path="/app/decisions/underperforming" element={<UnderperformingVehicles />} />
          <Route path="/app/decisions/comparisons" element={<VehicleComparisons />} />
          <Route path="/app/decisions/profit" element={<ProfitOpportunities />} />
          <Route path="/app/decisions/attention" element={<AttentionRequired />} />
          <Route path="/app/decisions/risk" element={<WhatCouldGoWrong />} />
          <Route path="/app/decisions/alerts" element={<AlertsExceptions />} />
          
          <Route path="/app/decisions/:type" element={<DecisionPlaceholder />} />
          <Route path="/app/operations/overview" element={<FleetOverview />} />
          <Route path="/admin" element={<AdminUsers />} />
          <Route path="/admin/users/:userId" element={<AdminUserDetail />} />
          <Route path="/admin/playbooks" element={<AdminPlaybooks />} />
          <Route path="/admin/playbooks/:id" element={<AdminPlaybookEdit />} />
          <Route path="/admin/playbooks/:id/nodes" element={<AdminPlaybookNodes />} />
          
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AppContent />
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
